import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { t, getLocale } from './i18n';

// ============================================================
// Tabul „De lucru" din Inbox (etapa 7, bucata 5 — 15 octombrie 2026).
//
// Ce i-a lasat filtrul de comentarii omului, in trei liste, din jurnalul filtrului:
//   - De raspuns public: ciorna scrisa singura (in vocea clientului), de trimis, schimbat sau aruncat;
//   - De hotarat: ce nu hotaraste filtrul singur (tema bolii, dezamagirea, ironia care acuza...);
//   - In privat, de un om: cazurile personale (o cerere, o reclamatie).
// Hotararile ei (14 octombrie): nimic nu se publica fara ea; ciorna neatinsa 3 zile dispare.
// Trimiterea si ascunderea trec prin aceleasi functii ca butoanele din postare.
// Doar super adminul: jurnalul filtrului e al ei (politica `moderare_jurnal`).
// ============================================================

const ZILE_CIORNA = 3;
const ZILE_LISTE = 7;

const C = { primar: '#1A6B3C', inchis: '#0F3D22', text: '#1F2937', sters: '#4B5563', chenar: '#D1E8DA', alarma: '#B42318' };

async function detaliu(error) {
  try {
    const ctx = error && error.context;
    if (ctx && typeof ctx.json === 'function') {
      const corp = await ctx.json();
      return String(corp.detail && corp.detail.message ? corp.detail.message : (corp.detail || corp.error || error.message));
    }
  } catch (_e) { /* mai jos */ }
  return (error && error.message) || '';
}

export function TabDeLucru({ clientPreSelectat }) {
  const [randuri, setRanduri] = useState(null);
  const [lista, setLista] = useState('public');
  const [texte, setTexte] = useState({});
  const [ocupat, setOcupat] = useState(null);

  const incarca = useCallback(async () => {
    const deLa = new Date(Date.now() - ZILE_LISTE * 86400000).toISOString();
    let q = supabase.from('moderare_jurnal')
      .select('id, client_id, mesaj_id, la, clasa, cuvine, regula')
      .is('rezolvat_la', null)
      .gte('la', deLa)
      .or('cuvine.in.(raspuns_public,privat_om),clasa.eq.de_aratat')
      .order('la', { ascending: false })
      .limit(500);
    if (clientPreSelectat) q = q.eq('client_id', clientPreSelectat);
    const { data } = await q;
    const r = data || [];
    const ids = r.map(x => x.mesaj_id).filter(Boolean);
    let m = [];
    if (ids.length) {
      const { data: d } = await supabase.from('mesaje')
        .select('id, autor, text, reactii, retea, client, postare, propunere, raspuns_trimis_la, raspuns_text, raspuns_privat_la, ascuns, scris_la')
        .in('id', ids);
      m = d || [];
    }
    setTexte(Object.fromEntries(m.map(x => [x.id, x])));
    setRanduri(r);
  }, [clientPreSelectat]);

  useEffect(() => { incarca(); }, [incarca]);

  if (randuri === null) return <div style={{ color: C.sters }}>{t('deLucru', 'seIncarca')}</div>;

  const prag3 = Date.now() - ZILE_CIORNA * 86400000;
  const viu = r => texte[r.mesaj_id];
  const liste = {
    public: randuri.filter(r => r.cuvine === 'raspuns_public' && viu(r) && !viu(r).raspuns_trimis_la && new Date(r.la).getTime() >= prag3),
    hotarat: randuri.filter(r => r.clasa === 'de_aratat' && viu(r) && !viu(r).raspuns_trimis_la && !viu(r).ascuns),
    privat: randuri.filter(r => r.cuvine === 'privat_om' && viu(r) && !viu(r).raspuns_privat_la),
  };

  async function rezolva(r) {
    await supabase.from('moderare_jurnal').update({ rezolvat_la: new Date().toISOString() }).eq('id', r.id);
    setRanduri(prev => prev.filter(x => x.id !== r.id));
  }

  async function trimite(r, text, felul) {
    const m = texte[r.mesaj_id];
    if (!m || !text.trim()) return;
    setOcupat(r.id);
    const { error } = m.retea === 'instagram'
      ? await supabase.functions.invoke('instagram-reply', { body: { mesaj_id: m.id, raspuns_text: text } })
      : await supabase.functions.invoke('facebook-reply', { body: { mesaj_id: m.id, raspuns_text: text, mod: felul } });
    setOcupat(null);
    if (error) { alert(t('deLucru', 'nuAPlecat', { eroare: await detaliu(error) })); return; }
    await rezolva(r);
  }

  async function ascunde(r) {
    const m = texte[r.mesaj_id];
    if (!m) return;
    setOcupat(r.id);
    const { error } = m.retea === 'instagram'
      ? await supabase.functions.invoke('instagram-reply', { body: { mesaj_id: m.id, ascunde: true } })
      : await supabase.functions.invoke('facebook-moderate', { body: { mesaj_id: m.id, actiune: 'ascunde' } });
    setOcupat(null);
    if (error) { alert(t('deLucru', 'nuAPlecat', { eroare: await detaliu(error) })); return; }
    await rezolva(r);
  }

  const vazute = liste[lista];
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        {['public', 'hotarat', 'privat'].map(id => (
          <button key={id} type="button" onClick={() => setLista(id)}
            style={{ border: '1px solid ' + (lista === id ? C.inchis : C.chenar), background: lista === id ? C.inchis : '#fff', color: lista === id ? '#fff' : C.text, borderRadius: 999, padding: '6px 14px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
            {t('deLucru', 'lista_' + id)} {liste[id].length}
          </button>
        ))}
      </div>
      <div style={{ fontSize: 13, color: C.sters, marginBottom: 14 }}>{t('deLucru', 'explicatie_' + lista)}</div>
      {!vazute.length && (
        <div style={{ background: '#F4FAF6', border: '1px solid ' + C.chenar, borderRadius: 12, padding: 20, color: C.text }}>
          {t('deLucru', 'gol_' + lista)}
        </div>
      )}
      {vazute.map(r => (
        <Rand key={r.id} r={r} m={texte[r.mesaj_id]} lista={lista} ocupat={ocupat === r.id}
          onTrimite={trimite} onAscunde={ascunde} onRezolva={rezolva} arataClientul={!clientPreSelectat} />
      ))}
    </div>
  );
}

function Rand({ r, m, lista, ocupat, onTrimite, onAscunde, onRezolva, arataClientul }) {
  const areCiorna = !!(m.propunere && !m.propunere.startsWith('(nu răspund)'));
  const [text, setText] = useState(areCiorna ? m.propunere : '');
  const [deschis, setDeschis] = useState(lista === 'public');
  const eInstagram = m.retea === 'instagram';
  const buton = (eticheta, onClick, principal = false, rosu = false) => (
    <button type="button" disabled={ocupat} onClick={onClick}
      style={{ border: principal ? 'none' : '1px solid ' + (rosu ? C.alarma : C.chenar), background: principal ? C.primar : '#fff', color: principal ? '#fff' : (rosu ? C.alarma : C.text), borderRadius: 999, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: ocupat ? 'default' : 'pointer', opacity: ocupat ? 0.5 : 1, fontFamily: 'inherit' }}>
      {eticheta}
    </button>
  );
  return (
    <div style={{ background: '#fff', border: '1px solid ' + C.chenar, borderRadius: 12, padding: 14, marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: C.sters, marginBottom: 4 }}>
        {arataClientul && m.client ? m.client + ' · ' : ''}{m.autor}{m.reactii ? ` · ${m.reactii} ${t('deLucru', 'reactii')}` : ''}
        {' · '}{new Date(m.scris_la || r.la).toLocaleString(getLocale(), { timeZone: 'Europe/Bucharest', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
        {m.postare ? ' · ' + t('deLucru', 'la') + ' „' + String(m.postare).slice(0, 60) + '…”' : ''}
      </div>
      <div style={{ fontSize: 14.5, color: C.text, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{m.text}</div>
      {r.regula && <div style={{ fontSize: 12, color: C.sters, marginTop: 4 }}>{t('deLucru', 'deCe')} {r.regula}</div>}

      {lista === 'public' && !areCiorna && !m.propunere && (
        <div style={{ fontSize: 13, color: C.sters, marginTop: 8 }}>{t('deLucru', 'ciornaSeScrie')}</div>
      )}
      {lista === 'public' && m.propunere && m.propunere.startsWith('(nu răspund)') && (
        <div style={{ fontSize: 13, color: C.sters, marginTop: 8 }}>{m.propunere}</div>
      )}

      {deschis && (
        <textarea value={text} onChange={e => setText(e.target.value)} rows={3}
          placeholder={t('deLucru', lista === 'privat' ? 'scrieInPrivat' : 'scrieRaspuns')}
          style={{ width: '100%', boxSizing: 'border-box', marginTop: 10, border: '1px dashed ' + C.primar, background: '#F2F8F4', borderRadius: 10, padding: 10, fontSize: 14, fontFamily: 'inherit', color: C.text, resize: 'vertical' }} />
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
        {lista === 'public' && <>
          {buton(t('deLucru', 'trimite'), () => onTrimite(r, text, 'public'), true)}
          {buton(t('deLucru', 'arunca'), () => onRezolva(r))}
        </>}
        {lista === 'hotarat' && <>
          {buton(t('deLucru', 'ascunde'), () => onAscunde(r), false, true)}
          {buton(t('deLucru', 'lasa'), () => onRezolva(r))}
          {!deschis
            ? buton(t('deLucru', 'raspunde'), () => setDeschis(true))
            : buton(t('deLucru', 'trimite'), () => onTrimite(r, text, 'public'), true)}
        </>}
        {lista === 'privat' && <>
          {eInstagram
            ? <span style={{ fontSize: 13, color: C.sters, alignSelf: 'center' }}>{t('deLucru', 'privatInstagram')}</span>
            : (!deschis
              ? buton(t('deLucru', 'scriePrivat'), () => setDeschis(true), true)
              : buton(t('deLucru', 'trimitePrivat'), () => onTrimite(r, text, 'privat'), true))}
          {buton(t('deLucru', 'gata'), () => onRezolva(r))}
        </>}
      </div>
    </div>
  );
}
